import type { PublicUser, ReservationListItem, ReservationListResult } from './api/types'

// ── Client-side DEMO accounts ───────────────────────────────────────────────────────────────
// Two showcase logins that work WITHOUT a real backend account and show static bookings.
// This is purely additive: only these exact email+password pairs are intercepted — every other
// login falls through to the normal /security flow untouched. Nothing is ever written to the
// server for a demo session.

const DEMO_KEY = 'gsm_demo_email'

export interface DemoAccount {
  email: string
  password: string
  user: PublicUser
  reservations: ReservationListResult
}

const ONLINE = { label: 'Carta di credito', type: 'ONLINE' as const }
const ONSITE = { label: 'In struttura', type: 'ONSITE' as const }

function booking(
  code: string,
  status: string,
  vehicle: string,
  plate: string,
  from: [string, string],
  to: [string, string],
  priceCents: number,
  pay: { label: string; type: 'ONLINE' | 'ONSITE' },
): ReservationListItem {
  return {
    code,
    cancellationCode: 'DEMO',
    status,
    arrivalDay: from[0],
    arrivalTime: from[1],
    departureDay: to[0],
    departureTime: to[1],
    duration: 0,
    totalPrice: priceCents,
    requireBilling: false,
    customerData: { licensePlate: plate },
    transportData: { label: vehicle },
    serviceData: null,
    conventionData: null,
    paymentModeData: pay,
  }
}

const ACCOUNTS: DemoAccount[] = [
  {
    email: 'filippo@leodaricreative.com',
    password: 'admin123',
    user: {
      name: 'Filippo',
      lastname: 'Verdi',
      email: 'filippo@leodaricreative.com',
      phoneNumber: '+39 333 1234567',
      licensePlate: 'AB123CD',
      country: 'IT',
    },
    reservations: {
      activeList: [
        booking('GSM-7F2A91', 'CONFIRMED', 'Auto', 'AB123CD', ['2026-06-20', '09:00:00'], ['2026-06-23', '18:00:00'], 13500, ONLINE),
        booking('GSM-7F3B12', 'CONFIRMED', 'Moto', 'MI456ON', ['2026-07-04', '10:00:00'], ['2026-07-06', '16:00:00'], 4800, ONSITE),
      ],
      archivedList: [
        booking('GSM-6A1C70', 'CONFIRMED', 'Auto', 'AB123CD', ['2026-05-10', '08:30:00'], ['2026-05-12', '12:00:00'], 9000, ONLINE),
      ],
    },
  },
  {
    email: 'ronaldo@leodaricreative.com',
    password: 'admin123',
    user: {
      name: 'Ronaldo',
      lastname: 'Bianchi',
      email: 'ronaldo@leodaricreative.com',
      phoneNumber: '+39 347 7654321',
      licensePlate: 'RM007RO',
      country: 'IT',
    },
    reservations: {
      activeList: [
        booking('GSM-9C7D21', 'CONFIRMED', 'Auto', 'RM007RO', ['2026-07-01', '14:00:00'], ['2026-07-05', '11:00:00'], 18000, ONLINE),
      ],
      archivedList: [
        booking('GSM-5B2E88', 'CONFIRMED', 'Auto', 'RM007RO', ['2026-04-15', '09:00:00'], ['2026-04-16', '19:00:00'], 4500, ONSITE),
        booking('GSM-4D9F03', 'CANCELLED', 'Moto', 'RM007RO', ['2026-03-02', '07:00:00'], ['2026-03-03', '20:00:00'], 3000, ONLINE),
      ],
    },
  },
]

// Stamp every demo booking's customer with the account holder, so the ticket "Cliente" isn't empty.
for (const acc of ACCOUNTS) {
  for (const b of [...acc.reservations.activeList, ...acc.reservations.archivedList]) {
    b.customerData = { name: acc.user.name, lastname: acc.user.lastname, licensePlate: b.customerData?.licensePlate }
  }
}

const norm = (e?: string | null) => (e ?? '').trim().toLowerCase()

/** A demo account whose email+password match exactly, or null. */
export function matchDemoLogin(email: string, password: string): DemoAccount | null {
  const e = norm(email)
  return ACCOUNTS.find((a) => a.email === e && a.password === password) ?? null
}

/** The demo account for an email (any password), or null. */
export function demoByEmail(email?: string | null): DemoAccount | null {
  const e = norm(email)
  return e ? ACCOUNTS.find((a) => a.email === e) ?? null : null
}

export function isDemoEmail(email?: string | null): boolean {
  return demoByEmail(email) != null
}

export function rememberDemo(email: string): void {
  try {
    localStorage.setItem(DEMO_KEY, norm(email))
  } catch {
    /* ignore */
  }
}

export function clearDemo(): void {
  try {
    localStorage.removeItem(DEMO_KEY)
  } catch {
    /* ignore */
  }
}

/** The demo account restored from a previous session, or null. */
export function storedDemo(): DemoAccount | null {
  try {
    return demoByEmail(localStorage.getItem(DEMO_KEY))
  } catch {
    return null
  }
}
